import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Workflow,
  Network,
  ClipboardList,
  UserCheck,
  Plug,
  ShieldAlert,
  Compass,
  Wrench,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import WebCenterStackDiagram from '@/components/diagrams/WebCenterStackDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const PAGE_TITLE =
  'Oracle SOA Suite Migration — 12c to 14c, Same December 2026 Cliff | EZ Cloud';
const PAGE_DESCRIPTION =
  'Oracle SOA Suite 12c (12.2.1.4) Premier Support ends December 2026 — the same cliff as WebCenter. SOA Suite composites drive your WebCenter AP workflow, EBS integration, and Fusion middleware tier. The recommended forward path is the upgrade to SOA Suite 14c (14.1.2 LTS). EZ Cloud delivers SOA composite inventory, migration planning, and 14c upgrade execution.';
const PAGE_CANONICAL = 'https://ezcloud.ai/solutions/oracle-soa-suite-migration';
const PAGE_DATE = '2026-06-29';

const SCHEMA_ABOUT = [
  'Oracle SOA Suite',
  'Oracle SOA Suite 12c',
  'Oracle SOA Suite 14c',
  'BPEL',
  'Oracle Mediator',
  'Oracle Business Rules',
  'Oracle Human Workflow',
  'Oracle Integration Cloud',
];

const KEY_FACTS = [
  {
    date: 'December 2026',
    label: 'SOA Suite 12c (12.2.1.4) Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c — including Oracle SOA Suite 12.2.1.4 — exits Premier Support on the same date as WebCenter. After this date there are no further OS certifications or non-critical updates for the SOA Suite tier that sits underneath WebCenter AP workflow and EBS / Fusion integration.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'SOA Suite 12c Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy. Extended Support provides security updates and critical patches but not general bug fixes — and only carries 12 months of runway. After December 2027 the only Oracle-provided option is Sustaining Support (limited patching) or Market Driven Support (yearly-renewable, higher-cost).',
    severity: 'red',
  },
  {
    date: '2030 / 2033',
    label: 'SOA Suite 14c (14.1.2 LTS) support window',
    detail:
      'Oracle SOA Suite 14c (14.1.2 LTS) is the designated Long-Term Support release. Premier Support runs through December 2030 and Extended Support through December 2033 — confirming Oracle\'s long-term commitment to SOA Suite as a strategic Fusion Middleware product.',
    severity: 'green',
  },
];

const WHAT_SOA_IS = [
  {
    icon: Workflow,
    title: 'BPEL processes',
    detail:
      'Business Process Execution Language composites orchestrate the invoice flow — from WebCenter Imaging and Forms Recognition through validation, coding, and routing into the ERP. The BPEL engine is where the AP business logic actually lives.',
  },
  {
    icon: Network,
    title: 'Mediator routing',
    detail:
      'Oracle Mediator handles message routing, transformation (XSLT), and filtering between composites. In a typical WebCenter AP install, Mediator is what stitches Capture, WFR, Imaging, and the ERP adapter together.',
  },
  {
    icon: ClipboardList,
    title: 'Business Rules dictionaries',
    detail:
      'Oracle Business Rules holds the declarative AP routing logic — approval thresholds, coding rules, exception handling. Often heavily customized over a decade of AP policy changes, with rules that nobody on the current team originally authored.',
  },
  {
    icon: UserCheck,
    title: 'Human Workflow tasks',
    detail:
      'The Human Workflow component is the AP approval engine — task assignment, escalation, delegation, and the worklist UI that approvers actually interact with. AME-style approval hierarchies are typically modeled here.',
  },
  {
    icon: Plug,
    title: 'EBS, JMS, and File Adapters',
    detail:
      'Technology adapters connect SOA composites to E-Business Suite (the EBS Adapter calls concurrent programs, queries open interfaces, and posts invoices), JMS queues, file drops, database tables, and Fusion via Web Services or OIC.',
  },
  {
    icon: Compass,
    title: 'The integration tier between Capture and ERP',
    detail:
      'In a typical WebCenter AP architecture, SOA Suite is the tier that routes between Capture / WFR / Imaging on one side and Oracle EBS or Fusion on the other. Without SOA, the AP automation flow does not move invoices into the ERP.',
  },
];

const STAKES = [
  {
    title: 'Same Premier Support cliff as WebCenter',
    body: 'Oracle SOA Suite 12.2.1.4 and Oracle WebCenter 12c share the Fusion Middleware 12c lifecycle. Premier Support ends December 2026; Extended Support ends December 2027. If you are scoping a WebCenter decision and your AP flow runs through SOA composites, you are scoping two products on the same clock, not one.',
  },
  {
    title: 'The tier nobody talks about until it breaks',
    body: 'WebCenter Content, Imaging, and Forms Recognition are visible to the AP team — they are the screens people use every day. SOA Suite sits in the middle and is invisible until a composite faults, an adapter loses connectivity, or a Business Rules change blocks invoice routing. The Dec 2026 cliff applies equally to that invisible tier.',
  },
  {
    title: 'Adapter compatibility with future EBS and Fusion releases',
    body: 'The EBS Adapter, OIC connectors, and database adapters that sit between SOA Suite and the ERP need ongoing certification against ERP releases. After Premier Support ends, certification of new EBS R12.2 patch levels, Fusion Cloud quarterly releases, and OS upgrades stops. The risk is not immediate failure — it is gradual loss of supported alignment with the rest of the Oracle stack.',
  },
];

const UPGRADE_PATH = [
  {
    step: '1',
    title: 'Composite inventory and dependency map',
    body: 'Document every BPEL process, Mediator routing rule, Business Rules dictionary, Human Workflow task definition, and adapter configuration in the current 12c domain. For each composite, capture upstream callers (Capture, WFR, Imaging) and downstream targets (EBS Adapter, OIC, JMS queues). The output is the inventory that drives the migration plan and the test plan.',
  },
  {
    step: '2',
    title: 'Schema upgrade and out-of-place 14c domain build',
    body: 'Oracle\'s recommended 12c → 14c pattern is out-of-place: a new SOA Suite 14c (14.1.2) domain is built alongside the existing 12c domain. The SOAINFRA schema and supporting MDS / OPSS schemas are upgraded using Oracle\'s Repository Creation Utility (RCU) and Upgrade Assistant against a 14c-compatible database (typically 19c or 23ai).',
  },
  {
    step: '3',
    title: 'Composite redeployment and compatibility validation',
    body: 'Each composite SAR is redeployed into the 14c domain and validated. JDeveloper 14c is used to refresh project compatibility, regenerate WSDL references, and resolve any deprecated activity types. BPEL and Mediator generally migrate forward cleanly; deprecated adapters and custom Java callouts need explicit attention. Note: the SOA Core Extension that some 12c installs depended on is not supported in 14c, so any composites using it require remediation.',
  },
  {
    step: '4',
    title: 'Adapter and integration re-test',
    body: 'The EBS Adapter, OIC connectors, JMS Adapter, File Adapter, and Database Adapter configurations are recreated in the 14c domain. Every WebCenter AP integration path is re-tested end-to-end: Capture → WFR → Imaging → SOA composites → EBS or Fusion. AME workflows referenced by Human Workflow tasks are validated against any AP routing logic changes.',
  },
  {
    step: '5',
    title: 'Parallel run, UAT, cutover',
    body: 'The 12c and 14c SOA domains run in parallel during UAT. AP invoice samples flow through both domains; outcomes are compared. Cutover happens only after Human Workflow assignment, Business Rules evaluation, adapter behavior, and ERP posting have all been validated against production-equivalent invoice volume.',
  },
];

const WHAT_MIGRATES = [
  {
    title: 'BPEL processes',
    detail:
      'The bulk of AP orchestration logic migrates forward via JDeveloper project upgrade. Activities, partner links, fault handlers, and correlation sets are preserved.',
  },
  {
    title: 'Mediator routing rules',
    detail:
      'Mediator components — including XSLT transformations and filter conditions — migrate cleanly. Routing topology is preserved.',
  },
  {
    title: 'Business Rules dictionaries',
    detail:
      'Rule dictionaries migrate forward via Oracle Business Rules SDK. Rulesets, decision tables, and rule functions transfer with validation against any deprecated APIs.',
  },
  {
    title: 'Human Workflow tasks (AP approvals)',
    detail:
      'Task definitions, assignment rules, escalation policies, and notification settings migrate forward. The Worklist application is rebuilt against the 14c task service.',
  },
  {
    title: 'File and JMS adapters',
    detail:
      'File polling, JMS produce and consume operations, and corresponding JCA configurations are recreated in the 14c domain. Behavior is consistent across versions.',
  },
  {
    title: 'EBS Adapter integrations',
    detail:
      'Concurrent program invocations, open interface inserts, business event subscriptions, and PL/SQL API calls into EBS Payables are recreated and re-tested. EBS schema and patch level are validated against 14c EBS Adapter certification.',
  },
];

const SPECIAL_ATTENTION = [
  {
    title: 'Deprecated adapters',
    detail:
      'Adapters that were marked deprecated in late-12c updates are removed or unsupported in 14c. Examples include certain legacy socket and legacy MQ patterns. Each deprecated adapter use needs an explicit forward decision.',
  },
  {
    title: 'Custom Java callouts',
    detail:
      'BPEL Java embeddings and Spring component implementations compiled against 12c SCA APIs require recompilation against 14c. Class references that depended on internal Oracle packages may need rework.',
  },
  {
    title: 'Deprecated patterns inherited from 11g',
    detail:
      'Composites that were originally authored in 11g and lifted into 12c may carry forward patterns (older BPMN constructs, legacy correlation styles, certain notification configurations) that need modernization before they will run cleanly on 14c.',
  },
  {
    title: 'SOA Core Extension dependencies',
    detail:
      'The SOA Core Extension component pattern used in some 12c installs is not supported in 14c. Composites that depend on it require remediation — either reworked onto supported 14c constructs or refactored to a different integration approach.',
  },
];

const SCENARIOS = [
  {
    title: 'WebCenter AP customers who don\'t realize SOA is on the same clock',
    body: 'The conversation typically starts about WebCenter — Content, Imaging, Forms Recognition. SOA Suite gets surfaced once we ask how invoices actually post into EBS or Fusion. The answer is almost always: through BPEL composites and the EBS Adapter. Same December 2026 cliff. Same decision window.',
  },
  {
    title: '11g SOA customers who skipped 12c',
    body: 'Some shops are still running SOA Suite 11g composites in production, having postponed the 11g → 12c upgrade for years. For them, December 2026 is the second cliff in a row. The migration path runs through 12c-compatible composite refactoring before the out-of-place 14c domain build can proceed.',
  },
  {
    title: 'Heavy customization in Business Rules and BPEL',
    body: 'A decade of AP policy changes layered into Business Rules dictionaries and BPEL fault-handling. The original implementers may no longer be available. Documentation is partial or missing. The 14c upgrade is the moment to do the composite archeology that has been deferred for years.',
  },
  {
    title: 'BPM Suite composites mixed in with the SOA Suite footprint',
    body: 'Some installs have Oracle BPM Suite components — BPMN processes, custom Human Tasks, BAM dashboards — running alongside SOA Suite composites in the same Fusion Middleware domain. The 14c migration plan needs to scope BPM Suite separately and confirm forward path for BPMN-based AP flows.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    badge: 'Tier 1 · Standard Consulting',
    title: 'Fixed-fee SOA composite inventory and 14c migration plan',
    body: 'A two-to-four week engagement that delivers a structured inventory of every BPEL process, Mediator routing rule, Business Rules dictionary, Human Workflow task, and adapter configuration in the current 12c domain — and a forward plan to SOA Suite 14c (14.1.2 LTS). Plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    badge: 'Tier 2 · Standard Consulting',
    title: '14c domain build, composite redeployment, and UAT',
    body: 'Fixed-fee project: new SOA Suite 14c domain built alongside the 12c domain, schemas upgraded via RCU and Upgrade Assistant, every composite redeployed and validated, adapter integrations recreated and re-tested, parallel run through UAT against production-equivalent invoice volume, and cutover support.',
  },
  {
    badge: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the SOA migration window',
    body: 'Monthly retainer with Andrew for customers working through a 6-12 month SOA Suite decision in parallel with the broader WebCenter decision. Strategic guidance on composite architecture choices, adapter forward paths, BPM Suite scope, and the interaction between the SOA and WebCenter upgrade workstreams.',
  },
  {
    badge: 'Tier 4 · Forward Deployed Engineering',
    title: 'Composite archeology and BPEL reverse-engineering',
    body: 'Forward Deployed Engineering for the harder cases — composites whose original authors are gone, Business Rules dictionaries that nobody on the current team can read, BPEL with embedded Java callouts and undocumented partner links. Andrew reads the deployed composites directly and reconstructs the intent. Output: documentation + a forward decision per composite.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle SOA Suite still strategic for Oracle?',
    a: 'Yes — Oracle SOA Suite 14c (14.1.2) is the designated Long-Term Support release in the Fusion Middleware 14c family. Premier Support runs through December 2030 and Extended Support through December 2033. The 14.1.2 LTS designation is Oracle\'s explicit signal that SOA Suite remains a strategic on-premises and OCI integration product alongside Oracle Integration Cloud, not a deprecated tier.',
  },
  {
    q: 'What\'s the 12c → 14c SOA Suite upgrade pattern?',
    a: 'The Oracle-recommended pattern is out-of-place — a new SOA Suite 14c domain is built alongside the existing 12c domain. The SOAINFRA, MDS, and OPSS schemas are upgraded using Oracle\'s Repository Creation Utility (RCU) and Upgrade Assistant against a 14c-compatible database (typically 19c or 23ai). Composites are then redeployed into the 14c domain via JDeveloper 14c, adapter configurations are recreated, and the 12c and 14c domains run in parallel through UAT before cutover. In-place upgrades are not recommended for 12c → 14c.',
  },
  {
    q: 'What about Oracle BPM Suite?',
    a: 'Oracle BPM Suite shares the Fusion Middleware 12c lifecycle — Premier Support ends December 2026 alongside SOA Suite and WebCenter. If your AP flow includes BPMN processes, custom Human Tasks built on the BPM Suite Worklist, or BAM dashboards, BPM Suite scope must be inventoried separately from the SOA Suite scope. The forward path is BPM Suite 14c where supported, with composite-by-composite decisions for patterns that need remediation.',
  },
  {
    q: 'Can we migrate to Oracle Integration Cloud (OIC) instead?',
    a: 'OIC is a legitimate forward path for certain integration patterns — particularly straightforward adapter-based EBS or Fusion connectivity — but it is not a one-to-one replacement for a heavily-customized SOA Suite footprint with BPEL orchestration, Business Rules dictionaries, and Human Workflow approvals. The right answer is typically composite-by-composite: some flows simplify onto OIC, some stay on SOA Suite 14c, and some get rethought entirely. The inventory and dependency-map phase is what makes that decision concrete.',
  },
  {
    q: 'What happens to AME workflows referenced by SOA composites?',
    a: 'AME itself is not affected by the SOA Suite version — Oracle Approvals Management Engine rules live in the EBS database and are unchanged by a 12c → 14c upgrade. Those AME rules used by EBS AP (approval hierarchies, signing limits, escalation chains) are referenced by SOA composites through the EBS Adapter or PL/SQL callouts. What does need re-testing in the 14c migration is the call path: every composite that invokes AME-driven logic in EBS Payables runs through end-to-end validation in the new 14c domain.',
  },
  {
    q: 'How does the SOA upgrade interact with the WebCenter upgrade?',
    a: 'They share the same Fusion Middleware 12c lifecycle and the same December 2026 / 2027 cliff, so they should be sequenced together. In a WebCenter AP implementation, SOA composites are the tier between Capture / WFR / Imaging and the ERP — so a WebCenter 14c upgrade without a coordinated SOA 14c plan leaves the integration tier on the old support clock. We typically scope them together: shared inventory phase, coordinated domain build, integrated UAT. See our [WebCenter 14c upgrade] page for the WebCenter-specific path and the [modernization hub] for the full forward-path framework.',
  },
  {
    q: 'What is the typical timeline for a 12c → 14c SOA Suite upgrade?',
    a: 'Typical timelines range 4-9 months depending on composite count, customization depth, adapter footprint, and BPM Suite scope. Implementations with a small number of well-documented composites and standard adapter use land at the shorter end. Implementations with a decade of layered Business Rules customization, BPEL with embedded Java, BPM Suite components, and undocumented partner links land at the longer end. The 2-4 week inventory and dependency-map phase is what produces a defensible timeline for your specific footprint.',
  },
  {
    q: 'What about Sustaining Support cost versus the upgrade?',
    a: 'For most customers, the supported-runway math for SOA Suite 14c (Premier through 2030, Extended through 2033) compares favorably against several years of Sustaining or Market Driven Support. Sustaining Support remains available indefinitely after Extended Support ends, but provides only limited patching — no new bug fixes, no new OS or DB certifications, no new adapter certifications against future EBS or Fusion releases. Market Driven Support (yearly-renewable) is more comprehensive but higher-cost. The math improves further when the SOA upgrade is sequenced together with the WebCenter upgrade rather than scoped as two separate projects.',
  },
];

const OracleSoaSuiteMigration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: PAGE_DATE,
    dateModified: PAGE_DATE,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_CANONICAL,
    },
    about: SCHEMA_ABOUT.map((name) => ({ '@type': 'Thing', name })),
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to migrate Oracle SOA Suite from 12c to 14c',
    description: 'The Oracle SOA Suite 12c to 14c (14.1.2 LTS) migration pattern. Out-of-place upgrade, schema upgrade, domain reconfiguration, and composite redeployment.',
    totalTime: 'P9M',
    step: UPGRADE_PATH.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/oracle-soa-suite-migration#step-' + (idx + 1),
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={PAGE_CANONICAL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Oracle SOA Suite Migration',
            url: PAGE_CANONICAL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date={PAGE_DATE} />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Workflow className="w-4 h-4" aria-hidden />
              Oracle SOA Suite · 12c → 14c · December 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle SOA Suite migration
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                12c (12.2.1.4) to 14c (14.1.2 LTS). The same December 2026 cliff as WebCenter.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle SOA Suite 12c (12.2.1.4) Premier Support ends December 2026 — the same cliff
              as WebCenter. The recommended forward path is the upgrade to SOA Suite 14c (14.1.2
              LTS).
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('soa-suite-migration-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a SOA Suite Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle SOA Suite migration, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle SOA Suite is the Fusion Middleware integration and process-orchestration
                  product family — BPEL, Mediator, Business Rules, Human Workflow, and the
                  technology adapter library (EBS Adapter, JMS Adapter, File Adapter, Database
                  Adapter, and OIC connectors).
                </strong>{' '}
                In a typical Oracle WebCenter AP implementation, SOA Suite is the tier that routes
                between Capture / WFR / Imaging on one side and Oracle EBS or Fusion on the other.
              </p>
              <p>
                The <strong className="text-gray-900">December 2026 Premier Support cliff applies
                equally to Oracle SOA Suite 12c (12.2.1.4) as it does to WebCenter 12c</strong> —
                same Fusion Middleware 12c lifecycle, same Extended Support window ending December
                2027. SOA composites underpin WebCenter AP workflow, EBS / Fusion integration, and
                the broader Fusion Middleware tier. The risk surface is identical.
              </p>
              <p>
                The recommended forward path is the{' '}
                <strong className="text-gray-900">
                  out-of-place upgrade to Oracle SOA Suite 14c (14.1.2 LTS)
                </strong>
                . A new 14c domain is built alongside the 12c domain, SOAINFRA schemas are upgraded
                via RCU and Upgrade Assistant, and composites are redeployed and re-tested. 14.1.2
                is Oracle's designated Long-Term Support release — Premier Support through
                December 2030, Extended Support through December 2033.
              </p>
            </div>
          </div>
        </section>

        {/* What SOA Suite is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The integration tier
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What Oracle SOA Suite is in the WebCenter AP context
            </h2>
            <div className="max-w-5xl mx-auto mb-12">
              <WebCenterStackDiagram />
            </div>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Six components, one integration tier. SOA Suite is what routes between Capture,
              Forms Recognition, Imaging, and the ERP — and it carries the Fusion Middleware 12c
              support clock.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHAT_SOA_IS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's at stake / key facts */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's at stake at Dec 2026
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Same support timeline as WebCenter — the tier nobody talks about until it breaks
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Oracle SOA Suite 12c and Oracle WebCenter 12c are on the same Fusion Middleware
              lifecycle. Premier Support ends December 2026; Extended Support ends December 2027.
            </p>
            <div className="space-y-5 mb-12">
              {KEY_FACTS.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {STAKES.map((s) => (
                <Card key={s.title} className="p-6 bg-gray-50 border border-gray-200">
                  <h3 className="text-gray-900 font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* The forward path */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward path
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              SOA Suite 12c → 14c (14.1.2 LTS) — the out-of-place upgrade pattern
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Out-of-place 14c domain build, schema upgrade via RCU and Upgrade Assistant, composite
              redeployment, adapter re-test, parallel UAT, cutover. Compatibility considerations
              including the SOA Core Extension (not supported in 14c) are surfaced in the inventory
              phase.
            </p>
            <div className="space-y-5">
              {UPGRADE_PATH.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What migrates / what needs attention */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-10">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                  What typically migrates forward
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Composites that move forward cleanly
                </h2>
                <p className="text-gray-600 mb-6">
                  The bulk of a typical AP-focused SOA Suite footprint migrates forward via the
                  out-of-place pattern and JDeveloper 14c project upgrade.
                </p>
                <div className="space-y-4">
                  {WHAT_MIGRATES.map((item) => (
                    <Card
                      key={item.title}
                      className="p-5 bg-gray-50 border border-gray-200 shadow-sm"
                    >
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-700 leading-relaxed">{item.detail}</p>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                  What needs special attention
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Where the migration plan earns its keep
                </h2>
                <p className="text-gray-600 mb-6">
                  Deprecated adapters, custom Java callouts, patterns inherited from 11g, and the
                  SOA Core Extension all require explicit forward decisions before they will run
                  cleanly on 14c.
                </p>
                <div className="space-y-4">
                  {SPECIAL_ATTENTION.map((item) => (
                    <Card
                      key={item.title}
                      className="p-5 bg-amber-50 border border-amber-200 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <ShieldAlert
                          className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5"
                          aria-hidden
                        />
                        <div>
                          <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                          <p className="text-sm text-gray-700 leading-relaxed">{item.detail}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Common SOA Suite scenarios in the December 2026 window
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four shapes of SOA Suite conversation we see most often. Yours may combine several.
              Related context lives in our{' '}
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                WebCenter 12c end-of-support page
              </Link>
              .
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map((s) => (
                <Card key={s.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on SOA Suite migrations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement options for the SOA Suite 12c → 14c migration
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard SOA Consulting (fixed-fee migration plan, 14c domain build, composite
              redeployment, UAT) for the mainline path. Forward Deployed Engineering (composite
              archeology, BPEL reverse-engineering) for the harder cases where the original
              authors are gone and documentation is partial.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((tier) => (
                <Card key={tier.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {tier.badge}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tier.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{tier.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call. SOA Suite and
              WebCenter scopes are typically sequenced together — see our{' '}
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                WebCenter 14c upgrade page
              </Link>{' '}
              and the related{' '}
              <Link
                to="/solutions/oracle-weblogic-14c-upgrade"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                Oracle WebLogic 14c upgrade page
              </Link>{' '}
              for the rest of the Fusion Middleware 12c stack.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the December 2026 cliff, with
              the Fusion Middleware tier (WebCenter + SOA Suite + WebLogic) scoped together —
              including eight diagnostic questions and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the SOA Suite 12c → 14c migration
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-10 text-center text-sm text-gray-500">
              For the broader path framework, see the{' '}
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                Modernize Oracle WebCenter
              </Link>{' '}
              hub.
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="flex justify-center mb-4">
              <Wrench className="w-10 h-10 text-accent" aria-hidden />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your SOA Suite migration
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years specifically inside the Fusion Middleware stack —
              SOA Suite, BPEL, Mediator, Business Rules, Human Workflow, EBS Adapter, WebLogic,
              WebCenter Content, Imaging, Forms Recognition, Enterprise Capture, ADF, and BPM.
              Walk through your composites and the 14c migration plan in the context of your
              specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('soa-suite-migration-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a SOA Suite Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleSoaSuiteMigration;
